import React, { useState, useEffect, useRef } from 'react';
import { Search, User, LogOut, Settings, FileText, MessageSquare, X, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { configGeneralAPI } from '../../api';
import axios from 'axios';
import NotificacionesDropdown from '../NotificacionesDropdown';
import BuscadorExpedienteRapido from './BuscadorExpedienteRapido';
import VoiceSearchButton from '../VoiceSearchButton';

const API = process.env.REACT_APP_BACKEND_URL;

const HeaderBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [logoUrl, setLogoUrl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    loadLogo();
    
    // Escuchar evento de actualización de logo
    const handleLogoUpdate = () => loadLogo();
    window.addEventListener('logo-updated', handleLogoUpdate);
    
    return () => window.removeEventListener('logo-updated', handleLogoUpdate);
  }, []);

  const loadLogo = async () => {
    try {
      const res = await configGeneralAPI.getLogo();
      if (res.data.configured && res.data.url) {
        setLogoUrl(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      } else {
        setLogoUrl(null);
      }
    } catch (error) {
      console.error('Error loading logo:', error);
      setLogoUrl(null);
    }
  };

  // Cerrar resultados al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Búsqueda con debounce
  const handleSearch = (value) => {
    setSearchQuery(value);
    
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    if (value.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    
    searchTimeout.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await axios.get(`${API}/api/busqueda-global`, { params: { q: value, limit: 8 } });
        setSearchResults(res.data.resultados || []);
        setShowResults(true);
      } catch (error) {
        console.error('Error en búsqueda:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  const handleResultClick = (result) => {
    setShowResults(false);
    setSearchQuery('');
    navigate(result.url);
  };

  const getIconForType = (tipo) => {
    switch (tipo) {
      case 'expediente': return <FileText className="w-4 h-4 text-blue-500" />;
      case 'contacto': return <User className="w-4 h-4 text-green-500" />;
      case 'consulta': return <MessageSquare className="w-4 h-4 text-purple-500" />;
      default: return <Search className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <header className="bg-blue-700 text-white">
      <div className="flex items-center justify-between px-6 py-3">
        {/* Logo y Nombre */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="Logo" 
                className="h-8 w-auto max-w-[150px] object-contain"
              />
            ) : (
              <>
                <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
                  <span className="text-blue-700 font-bold text-lg">L</span>
                </div>
                <span className="text-xl font-bold">LEXDUO</span>
              </>
            )}
          </div>

          {/* Búsqueda rápida por nº de expediente */}
          <BuscadorExpedienteRapido />
        </div>

        {/* Búsqueda */}
        <div className="flex-1 max-w-xl mx-6" ref={searchRef}>
          <div className="relative flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowResults(true)}
                placeholder="Buscar expedientes, contactos, documentos..."
                className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white focus:text-gray-900 focus:placeholder:text-gray-400"
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
              )}
              {searchQuery && !isSearching && (
                <button
                  onClick={() => { setSearchQuery(''); setSearchResults([]); setShowResults(false); }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            
              {/* Resultados de búsqueda */}
              {showResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border max-h-96 overflow-y-auto z-50">
                  {searchResults.map((result, idx) => (
                    <div
                      key={`${result.tipo}-${result.id}-${idx}`}
                      onClick={() => handleResultClick(result)}
                      className="flex items-start gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                    >
                      <div className="mt-1">
                        {getIconForType(result.tipo)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{result.titulo}</p>
                        <p className="text-sm text-gray-600 truncate">{result.subtitulo}</p>
                        <p className="text-xs text-gray-400 truncate">{result.descripcion}</p>
                      </div>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded capitalize">
                        {result.tipo}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            
              {/* Sin resultados */}
              {showResults && searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border p-4 z-50">
                  <p className="text-gray-500 text-center">No se encontraron resultados para "{searchQuery}"</p>
                </div>
              )}
            </div>
            <VoiceSearchButton
              variant="header"
              onResult={(text) => {
                setSearchQuery(text);
                handleSearch(text);
              }}
              data-testid="voice-search-header"
            />
          </div>
        </div>

        {/* Acciones y Usuario */}
        <div className="flex items-center gap-3">
          <NotificacionesDropdown />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 text-white hover:bg-white/10">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5" />
                </div>
                <div className="text-left hidden md:block">
                  <div className="text-sm font-medium">{user?.nombre}</div>
                  <div className="text-xs opacity-80">{user?.rol}</div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Mi cuenta</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>{user?.email}</DropdownMenuItem>
              <DropdownMenuItem>{user?.despacho}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/configuracion')}>
                <Settings className="w-4 h-4 mr-2" />
                Configuración
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default HeaderBar;